import { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { ExpandModes } from 'utils/contexts/expansion';
import { DESTINATIONS_LIST } from '../constants/prequel';
import noop from 'utils/noop';
import { useDestinationVendors, EnumObject } from '@prequel/react';

export type DestinationPageMode = 'create' | 'edit';

type PrequelContextState = {
  modalExpandMode: ExpandModes;
  toggleIsModalShown: () => void;
  selectedDestination?: string;
  setSelectedDestination: (destination: string) => void;
  clearSelectedDestination: () => void;
  // Navigation methods
  navigateToCreate: (destinationId?: string) => void;
  navigateToEdit: (destinationId: string) => void;
  navigateToTransferHistory: (destinationId: string) => void;
  navigateToIndex: () => void;
  getVendorByKey: (key: string) => EnumObject | undefined;
};

// Initial states
export const initialPrequelContextState: PrequelContextState = {
  modalExpandMode: ExpandModes.collapse,
  toggleIsModalShown: noop,
  selectedDestination: DESTINATIONS_LIST.bigquery.id,
  setSelectedDestination: noop,
  clearSelectedDestination: noop,
  navigateToCreate: noop,
  navigateToEdit: noop,
  navigateToTransferHistory: noop,
  navigateToIndex: noop,
  getVendorByKey: () => undefined,
};

const PrequelContext = createContext<PrequelContextState | undefined>(undefined);

export const usePrequelContext = () => {
  const context = useContext(PrequelContext);
  if (!context) {
    throw new Error('usePrequelContext must be used within a PrequelContextProvider');
  }
  return context;
};

type PrequelContextProviderProps = {
  children: ReactNode;
};

export const PrequelContextProvider = ({ children }: PrequelContextProviderProps) => {
  const history = useHistory();

  const [modalExpandMode, setModalExpandMode] = useState(ExpandModes.collapse);
  const [selectedDestination, setSelectedDestination] = useState<string>(
    DESTINATIONS_LIST.bigquery.id,
  );

  const clearSelectedDestination = useCallback(() => {
    setSelectedDestination(DESTINATIONS_LIST.bigquery.id);
  }, []);

  const navigateToCreate = useCallback(
    (destinationId?: string) => {
      const destination = destinationId || selectedDestination || 'bigquery';
      if (destinationId) {
        setSelectedDestination(destinationId);
      }
      setModalExpandMode(ExpandModes.collapse); // Close modal if open
      history.push(`/integrations/dataSync/create/${destination}`);
    },
    [history, selectedDestination],
  );

  const navigateToEdit = useCallback(
    (destinationId: string) => {
      setModalExpandMode(ExpandModes.collapse); // Close modal if open
      history.push(`/integrations/dataSync/edit/${destinationId}`);
    },
    [history],
  );

  const navigateToTransferHistory = useCallback(
    (destinationId: string) => {
      setModalExpandMode(ExpandModes.collapse); // Close modal if open
      history.push(`/integrations/dataSync/transferHistory/${destinationId}`);
    },
    [history],
  );

  const navigateToIndex = useCallback(() => {
    clearSelectedDestination();
    history.push('/integrations/dataSync');
  }, [history, clearSelectedDestination]);

  const toggleIsModalShown = useCallback(() => {
    setModalExpandMode((current) => {
      return current === ExpandModes.collapse ? ExpandModes.expand : ExpandModes.collapse;
    });
  }, []);

  const handleSetSelectedDestination = useCallback((destination: string) => {
    setSelectedDestination(destination);
  }, []);

  const destinationVendors = useDestinationVendors();
  const getVendorByKey = useCallback(
    (key: string) => {
      return destinationVendors?.find((vendor) => vendor.key === key);
    },
    [destinationVendors],
  );

  const value: PrequelContextState = {
    modalExpandMode,
    toggleIsModalShown,
    selectedDestination,
    setSelectedDestination: handleSetSelectedDestination,
    clearSelectedDestination,
    navigateToCreate,
    navigateToEdit,
    navigateToTransferHistory,
    navigateToIndex,
    getVendorByKey,
  };

  return <PrequelContext.Provider value={value}>{children}</PrequelContext.Provider>;
};
